import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useTaskStore } from './taskStore';
import type { Task, TimeBlock } from '../types';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import {
  accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount, swappingAccount,
} from '../lib/accountStorage';
import { changedFields, createDocSync, type DocBase, type DocSync } from '../lib/syncDoc';
import { supabaseBlobTransport } from '../lib/syncBlobTransport';
import { loadedFor, refreshBlobOnFocus } from '../lib/blobFocusRefresh';

// ─── Supabase sync (sync epic) — synced/recurring blocks as a blob per user ──
// ⚠️⚠️ Writes go through syncDoc (lib/syncDoc.ts), NOT a whole-row upsert: the
// old upsert wrote this tab's copy of every block, so a stale tab brought back
// blocks removed elsewhere and undid other devices' task changes. Now a write
// re-reads the row and applies only the blocks THIS tab changed (by id; a delete
// beats an edit; a block changed on both sides keeps this tab's copy).
const USER_SYNCED_BLOCKS_TABLE = 'user_synced_blocks';

type SyncedBlockDoc = { syncedBlocks: SyncedBlock[] };

const pickBlocks = (s: Pick<SyncedBlockState, 'syncedBlocks'>): SyncedBlockDoc => ({ syncedBlocks: s.syncedBlocks });

function hydrateBlocks(data: unknown): SyncedBlockDoc {
  const d = (data && typeof data === 'object' && !Array.isArray(data) ? data : {}) as Record<string, unknown>;
  return { syncedBlocks: Array.isArray(d.syncedBlocks) ? (d.syncedBlocks as SyncedBlock[]) : [] };
}

const blockSync: DocSync<SyncedBlockDoc> = createDocSync<SyncedBlockDoc>({
  transport: supabaseBlobTransport(USER_SYNCED_BLOCKS_TABLE),
  owner: () => useSyncedBlockStore.getState().cachedUserId,
  rules: { syncedBlocks: { records: 'id' } },
  hydrate: hydrateBlocks,
  read: () => pickBlocks(useSyncedBlockStore.getState()),
  readBase: () => useSyncedBlockStore.getState().syncBase,
  adopt: (doc, base) => useSyncedBlockStore.setState((state: SyncedBlockState) => ({
    ...changedFields(pickBlocks(state), doc),
    syncBase: base,
  })),
  onError: (error) => console.error('Failed to sync synced-blocks to Supabase:', error),
});

/** Await the pending synced-blocks write (tests). */
export function flushSyncedBlockSync(): Promise<void> {
  return blockSync.flush();
}

// A tab that comes back shows blocks changed elsewhere (lib/blobFocusRefresh.ts).
refreshBlobOnFocus(USER_SYNCED_BLOCKS_TABLE, () => blockSync.refresh(loadedFor(() => useSyncedBlockStore.getState().initializedUserId)));

interface SyncedBlock {
  id: string;
  name: string;
  block: TimeBlock;
  tasks: Task[];
  category: string;
  templateBlockId: string;
  isSynced: boolean;
}

interface SyncedBlockState {
  syncedBlocks: SyncedBlock[];
  initializedUserId: string | null;
  cachedUserId: string | null;
  /** The last cloud copy this tab agreed with (hashed per block) — see lib/syncDoc.ts. */
  syncBase: DocBase<SyncedBlockDoc> | null;
  initialize: () => Promise<void>;
  addSyncedBlock: (block: TimeBlock, name: string) => string;
  removeSyncedBlock: (id: string) => void;
  getSyncedBlock: (id: string) => SyncedBlock | undefined;
  getBlocksByTemplateId: (templateBlockId: string) => SyncedBlock[];
  addTask: (blockId: string, task: Task) => void;
  removeTask: (blockId: string, taskId: string) => void;
  toggleTask: (blockId: string, taskId: string) => void;
  updateTask: (blockId: string, taskId: string, updates: Partial<Task>) => void;
  syncTasksToBlock: (blockId: string, tasks: Task[]) => void;
  updateBlockName: (blockId: string, name: string) => void;
  toggleBlockSync: (templateBlockId: string) => void;
  clearAllBlocks: () => void;
}

export const useSyncedBlockStore = create<SyncedBlockState>()(
  persist<SyncedBlockState, [], [], any>(
    (rawSet, get) => {
      // ⚠️ The first edit of a tab whose load never finished records its base
      // (see the same wrapper in userStore).
      const set: typeof rawSet = (...args: Parameters<typeof rawSet>) => {
        if (get().syncBase === null) rawSet({ syncBase: blockSync.baseOf(pickBlocks(get())) });
        return rawSet(...args);
      };
      return {
      syncedBlocks: [],
      initializedUserId: null,
      cachedUserId: null,
      syncBase: null,

      // The cloud wins for every block this tab has not changed since its base;
      // a cache that is another account's is replaced outright.
      initialize: () => dedupeInit('user_synced_blocks', () => blockSync.exclusive(async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          if (get().initializedUserId === user.id) return;
          // Cache-first boot: while this user's local cache is fresh, skip the
          // cloud read entirely (write-through keeps cloud current meanwhile).
          if (get().cachedUserId === user.id && isCloudFresh('user_synced_blocks', user.id)) {
            rawSet((state) => ({
              initializedUserId: user.id,
              syncBase: state.syncBase ?? blockSync.baseOf(pickBlocks(state)),
            }));
            return;
          }
          const { data, error } = await supabase.from(USER_SYNCED_BLOCKS_TABLE).select('*');
          // An answer for an account that has since signed out is not this copy's.
          if (at !== accountGeneration()) return;
          if (error) throw error;
          const row = (data ?? [])[0];
          const cloudData = row && row.data && Array.isArray(row.data.syncedBlocks) ? row.data : null;
          const state = get();
          const cacheIsOurs = state.cachedUserId === null || state.cachedUserId === user.id;
          let unsaved = false;
          if (cacheIsOurs) unsaved = blockSync.reconcile(cloudData);
          else blockSync.replace(cloudData);
          blockSync.noteLoaded(row?.updated_at ?? null);
          rawSet({ initializedUserId: user.id, cachedUserId: user.id });
          markCloudFresh('user_synced_blocks', user.id);
          if (unsaved) blockSync.schedule();
        } catch (error) {
          console.error('Failed to initialize synced-blocks from Supabase:', error);
        }
      })),

      addSyncedBlock: (block, name) => {
        // Generate new IDs if not provided
        const id = block.syncId || crypto.randomUUID();
        const templateBlockId = block.templateBlockId || crypto.randomUUID();

        // Check if block already exists by templateBlockId
        const existingBlock = get().syncedBlocks.find(b => 
          b.templateBlockId === templateBlockId
        );

        if (existingBlock) {
          return existingBlock.id;
        }

        const newSyncedBlock: SyncedBlock = {
          id,
          name,
          block: { 
            ...block,
            syncId: id,
            templateBlockId,
            category: block.category
          },
          category: block.category,
          templateBlockId,
          tasks: [],
          isSynced: false // Default to not synced
        };

        set(state => ({
          syncedBlocks: [...state.syncedBlocks, newSyncedBlock],
        }));

        return id;
      },

      // Replace the tasks associated with a synced block (used when a template's
      // block tasks change and need to be re-synced).
      syncTasksToBlock: (blockId, tasks) => {
        set(state => ({
          syncedBlocks: state.syncedBlocks.map(b =>
            b.id === blockId ? { ...b, tasks } : b
          ),
        }));
      },

      removeSyncedBlock: (id) => {
        const block = get().syncedBlocks.find(b => b.id === id);
        if (!block) return;

        // Get all blocks with same templateBlockId
        const relatedBlocks = get().getBlocksByTemplateId(block.templateBlockId);

        // Update tasks in task store to unlink them
        const taskStore = useTaskStore.getState();
        block.tasks.forEach(task => {
          taskStore.updateTask(task.id, task.category ?? '', {
            blockId: undefined,
            syncId: undefined,
            status: 'categorized'
          });
        });

        // Remove the block from synced blocks
        set(state => ({
          syncedBlocks: state.syncedBlocks.filter(block => block.id !== id),
        }));
      },

      getSyncedBlock: (id) => {
        // First try to find by direct ID
        let block = get().syncedBlocks.find(block => block.id === id);
        
        // If not found, try to find by templateBlockId
        if (!block) {
          block = get().syncedBlocks.find(block => block.templateBlockId === id);
        }
        
        return block;
      },

      getBlocksByTemplateId: (templateBlockId) => {
        return get().syncedBlocks.filter(block => 
          block.templateBlockId === templateBlockId
        );
      },

      addTask: (blockId, task) => {
        const syncedBlock = get().syncedBlocks.find(b => b.id === blockId);
        if (!syncedBlock) return;

        // Add task to task store first
        const taskStore = useTaskStore.getState();
        const newTask: Task = {
          ...task,
          category: syncedBlock.category,
          status: 'scheduled',
          blockId: syncedBlock.block.id,
          templateBlockId: syncedBlock.templateBlockId,
          syncId: syncedBlock.isSynced ? syncedBlock.id : undefined
        };
        taskStore.addTask(newTask);

        // If block is synced, create copies for all related blocks
        if (syncedBlock.isSynced) {
          const relatedBlocks = get().getBlocksByTemplateId(syncedBlock.templateBlockId);
          relatedBlocks.forEach(relatedBlock => {
            if (relatedBlock.id !== syncedBlock.id) {
              const relatedTask = {
                ...newTask,
                id: crypto.randomUUID(),
                blockId: relatedBlock.block.id,
                syncId: syncedBlock.id
              };
              taskStore.addTask(relatedTask);
            }
          });
        }

        // Add to synced block
        set(state => ({
          syncedBlocks: state.syncedBlocks.map(block => 
            block.id === blockId
              ? { ...block, tasks: [...block.tasks, newTask] }
              : block
          )
        }));
      },

      removeTask: (blockId, taskId) => {
        const syncedBlock = get().syncedBlocks.find(b => b.id === blockId);
        if (!syncedBlock) return;

        // Get task from store
        const taskStore = useTaskStore.getState();
        const task = taskStore.getTaskById(taskId);
        if (!task) return;

        // If block is synced, remove all related tasks
        if (syncedBlock.isSynced) {
          const relatedBlocks = get().getBlocksByTemplateId(syncedBlock.templateBlockId);
          relatedBlocks.forEach(relatedBlock => {
            const relatedTasks = taskStore.tasks[task.category ?? '']?.filter(t =>
              t.syncId === syncedBlock.id
            ) || [];
            relatedTasks.forEach(relatedTask => {
              taskStore.deleteTask(relatedTask.id, task.category ?? '');
            });
          });
        } else {
          // Just remove this task
          taskStore.deleteTask(taskId, task.category ?? '');
        }

        // Remove from synced block
        set(state => ({
          syncedBlocks: state.syncedBlocks.map(block => 
            block.id === blockId
              ? { ...block, tasks: block.tasks.filter(t => t.id !== taskId) }
              : block
          )
        }));
      },

      toggleTask: (blockId, taskId) => {
        const syncedBlock = get().syncedBlocks.find(b => b.id === blockId);
        if (!syncedBlock) return;

        // Get task from store
        const taskStore = useTaskStore.getState();
        const task = taskStore.getTaskById(taskId);
        if (!task) return;

        // If block is synced, toggle all related tasks
        if (syncedBlock.isSynced) {
          const relatedBlocks = get().getBlocksByTemplateId(syncedBlock.templateBlockId);
          relatedBlocks.forEach(relatedBlock => {
            const relatedTasks = taskStore.tasks[task.category ?? '']?.filter(t =>
              t.syncId === syncedBlock.id
            ) || [];
            relatedTasks.forEach(relatedTask => {
              taskStore.toggleTask(relatedTask.id, task.category ?? '');
            });
          });
        } else {
          // Just toggle this task
          taskStore.toggleTask(taskId, task.category ?? '');
        }

        // Update synced block
        set(state => ({
          syncedBlocks: state.syncedBlocks.map(block => 
            block.id === blockId
              ? {
                  ...block,
                  tasks: block.tasks.map(t =>
                    t.id === taskId
                      ? { ...t, completed: !t.completed }
                      : t
                  )
                }
              : block
          )
        }));
      },

      updateTask: (blockId, taskId, updates) => {
        const syncedBlock = get().syncedBlocks.find(b => b.id === blockId);
        if (!syncedBlock) return;

        // Get task from store
        const taskStore = useTaskStore.getState();
        const task = taskStore.getTaskById(taskId);
        if (!task) return;

        // If block is synced, update all related tasks
        if (syncedBlock.isSynced) {
          const relatedBlocks = get().getBlocksByTemplateId(syncedBlock.templateBlockId);
          relatedBlocks.forEach(relatedBlock => {
            const relatedTasks = taskStore.tasks[task.category ?? '']?.filter(t =>
              t.syncId === syncedBlock.id
            ) || [];
            relatedTasks.forEach(relatedTask => {
              taskStore.updateTask(relatedTask.id, task.category ?? '', updates);
            });
          });
        } else {
          // Just update this task
          taskStore.updateTask(taskId, task.category ?? '', updates);
        }

        // Update synced block
        set(state => ({
          syncedBlocks: state.syncedBlocks.map(block => 
            block.id === blockId
              ? {
                  ...block,
                  tasks: block.tasks.map(t =>
                    t.id === taskId
                      ? { ...t, ...updates }
                      : t
                  )
                }
              : block
          )
        }));
      },

      updateBlockName: (blockId, name) => {
        const syncedBlock = get().syncedBlocks.find(b => b.id === blockId);
        if (!syncedBlock) return;

        // Update all blocks with same templateBlockId
        set(state => ({
          syncedBlocks: state.syncedBlocks.map(block => {
            if (block.templateBlockId === syncedBlock.templateBlockId) {
              return {
                ...block,
                name,
                block: {
                  ...block.block,
                  name
                }
              };
            }
            return block;
          })
        }));
      },

      toggleBlockSync: (templateBlockId) => {
        // Find all blocks with this templateBlockId
        const blocks = get().syncedBlocks.filter(b => 
          b.templateBlockId === templateBlockId
        );

        if (blocks.length === 0) return;

        // Get the first block as the reference
        const referenceBlock = blocks[0];
        const newSyncState = !referenceBlock.isSynced;

        // Update all blocks with the same templateBlockId
        set(state => ({
          syncedBlocks: state.syncedBlocks.map(block => {
            if (block.templateBlockId === templateBlockId) {
              return {
                ...block,
                isSynced: newSyncState
              };
            }
            return block;
          })
        }));

        // Get task store
        const taskStore = useTaskStore.getState();

        // Update tasks in all related blocks
        blocks.forEach(block => {
          const blockTasks = taskStore.tasks[block.category]?.filter(task => 
            task.blockId === block.block.id ||
            task.templateBlockId === block.templateBlockId ||
            task.syncId === block.id
          ) || [];

          // If enabling sync, create copies of tasks in all blocks
          if (newSyncState) {
            // Create a map of existing tasks by title to avoid duplicates
            const existingTasks = new Map<string, Task>();
            blockTasks.forEach(task => {
              existingTasks.set(task.title, task);
            });

            // Create copies for each block
            blocks.forEach(targetBlock => {
              if (targetBlock.id !== block.id) {
                existingTasks.forEach(task => {
                  const newTask = {
                    ...task,
                    id: crypto.randomUUID(),
                    blockId: targetBlock.block.id,
                    templateBlockId: targetBlock.templateBlockId,
                    syncId: block.id,
                    status: 'scheduled' as const
                  };
                  taskStore.addTask(newTask);
                });
              }
            });
          }

          // Update sync state for all tasks
          blockTasks.forEach(task => {
            taskStore.updateTask(task.id, task.category ?? '', {
              syncId: newSyncState ? block.id : undefined,
              status: 'scheduled'
            });
          });
        });
      },

      clearAllBlocks: () => {
        // Get all tasks from synced blocks
        const allTasks = get().syncedBlocks.flatMap(block => block.tasks);

        // Update tasks in task store to unlink them
        const taskStore = useTaskStore.getState();
        allTasks.forEach(task => {
          taskStore.updateTask(task.id, task.category ?? '', {
            blockId: undefined,
            syncId: undefined,
            status: 'categorized'
          });
        });

        // Clear all synced blocks
        set({ syncedBlocks: [] });
      },
      };
    },
    {
      name: 'synced-blocks-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 24,
      partialize: (state: SyncedBlockState) => ({
        syncedBlocks: state.syncedBlocks, cachedUserId: state.cachedUserId, syncBase: state.syncBase,
      }) as any,
      migrate: (persistedState: any, version: number) => {
        if (version < 24) {
          return { syncedBlocks: [] } as unknown as SyncedBlockState;
        }
        return persistedState as SyncedBlockState;
      }
    }
  )
);

// Auto-sync synced-blocks on change; a cloud copy being adopted is not a change of ours.
useSyncedBlockStore.subscribe((state, prev) => {
  if (swappingAccount()) return; // a change of account swaps the saved copy in — not an edit
  if (blockSync.adopting) return;
  if (state.syncedBlocks === prev.syncedBlocks) return;
  blockSync.schedule();
});

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: an account's edits whose
   write had not landed lived only until the next account's load replaced the copy, and the next
   account's edits made before its own load were merged into the last one's row by ITS next load. */
scopeToAccount(useSyncedBlockStore);
